// src/pages/OpportunityDetailPage.jsx
// C3.2.3.3 — Ficha Comercial 360 Lead Web ORBIS ancho completo

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { API_BASE } from "../config/orbisConfig";



const EMPTY_FORM = {
    id: null, name: "", code: "", account_id: "", description: "", notes: "",
    campaign_id: "", pipeline_id: "", pipeline_stage_id: "", product_line: "ORBIS PMO", lead_source: "",
    probability: "", expected_close_date: "", estimated_value: "", currency: "MXN", industry_id: "",
    pain_point: "", current_tool: "", active_projects_count: "", estimated_users: "",
    pilot_package: "", pilot_value: "", monthly_potential: "", decision_maker: "", urgency: "",
    next_action: "", next_action_date: "", status_comment: ""
};

const PILOT_PACKAGES = [["", "— Sin definir —"], ["base", "Piloto Base"], ["ejecutivo", "Piloto Ejecutivo"], ["institucional", "Piloto Institucional"], ["personalizado", "Personalizado"]];
const URGENCY_OPTIONS = [["", "— Sin definir —"], ["baja", "Baja"], ["media", "Media"], ["alta", "Alta"], ["critica", "Crítica"]];
const SOURCE_OPTIONS = [["", "— Sin definir —"], ["Landing ORBIS", "Landing ORBIS"], ["WhatsApp", "WhatsApp"], ["Email", "Email"], ["LinkedIn", "LinkedIn"], ["Referido", "Referido"], ["Contacto directo", "Contacto directo"], ["Evento / reunión", "Evento / reunión"]];
const PRODUCT_LINES = [
    "",
    "ORBIS PMO",
    "Gestión institucional",
    "CRM institucional",
    "Proyectos y compromisos",
    "Eventos institucionales",
    "Directorio estratégico",
    "Otro"
];
const PRODUCT_CAMPAIGN_RULES = {
    "ORBIS PMO": {
        campaignCodes: ["ORBIS-PMO-LANZAMIENTO", "ORBIS-PMO-GOBIERNO", "ORBIS-PMO-INDUSTRIA", "ORBIS-PMO-TEC-MKT"],
        pipelineCodes: ["VENTA-ORBIS-PMO"],
    },
    "Afiliaciones CANIETI": {
        campaignCodes: ["CANIETI-AFILIACIONES", "AFILIACIONES-CANIETI"],
        pipelineCodes: ["AFILIACIONES-CANIETI"],
    },
    "GreenDoc": {
        campaignCodes: ["GREENDOC", "ORBIS-PMO-GOBIERNO"],
        pipelineCodes: ["GOBIERNO-INSTITUCIONAL", "VENTA-ORBIS-PMO"],
    },
    "SuperLeads": {
        campaignCodes: ["SUPERLEADS", "ORBIS-PMO-TEC-MKT"],
        pipelineCodes: ["INDUSTRIA-EMPRESAS", "VENTA-ORBIS-PMO"],
    },
    "SITIC": {
        campaignCodes: ["SITIC", "ORBIS-PMO-TEC-MKT"],
        pipelineCodes: ["INDUSTRIA-EMPRESAS", "VENTA-ORBIS-PMO"],
    },
    "RH3": {
        campaignCodes: ["RH3", "INDUSTRIA-EMPRESAS"],
        pipelineCodes: ["INDUSTRIA-EMPRESAS", "VENTA-ORBIS-PMO"],
    },
};

function matchesProductCampaign(productLine, campaign) {
    const rules = PRODUCT_CAMPAIGN_RULES[productLine];
    if (!rules) return true;

    const code = String(campaign?.code || "").toUpperCase();
    const name = String(campaign?.name || "").toUpperCase();
    const targetCodes = rules.campaignCodes.map((c) => String(c).toUpperCase());

    return targetCodes.some((c) => code.includes(c) || name.includes(c.replaceAll("-", " ")));
}

function matchesProductPipeline(productLine, pipeline) {
    const rules = PRODUCT_CAMPAIGN_RULES[productLine];
    if (!rules) return true;

    const code = String(pipeline?.code || "").toUpperCase();
    const name = String(pipeline?.name || "").toUpperCase();
    const targetCodes = rules.pipelineCodes.map((c) => String(c).toUpperCase());

    return targetCodes.some((c) => code.includes(c) || name.includes(c.replaceAll("-", " ")));
}

function authHeaders(extra = {}) {
    const token = localStorage.getItem("cani_auth") || "";
    return { ...extra, Authorization: `Bearer ${token}`, "X-CANI-AUTH": token };
}

async function safeJson(url, options = {}) {
    const res = await fetch(url, options);
    const text = await res.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch { throw new Error(`Respuesta no JSON (${res.status}): ${text.slice(0, 200)}`); }
    if (!res.ok || data?.success === false || data?.ok === false) throw new Error(data?.error || data?.detail || `HTTP ${res.status}`);
    return data;
}

function asArray(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.industries)) return data.industries;
    return [];
}

function toDate(v) { return v ? String(v).slice(0, 10) : ""; }
function toDateTimeLocal(v) { return v ? String(v).replace(" ", "T").slice(0, 16) : ""; }
function normalizeDateTimeForApi(v) { if (!v) return ""; const x = v.replace("T", " "); return x.length === 16 ? `${x}:00` : x; }
function defaultDateTimeLocal(minutesFromNow = 30) {
    const d = new Date();
    d.setMinutes(d.getMinutes() + minutesFromNow);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
}
function formatCurrency(v, c = "MXN") {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return `${c || "MXN"} ${n.toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
}

function formatDateTime(value) {
    if (!value || String(value).startsWith("0000-00-00")) return "—";
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "—";

    const day = String(d.getDate()).padStart(2, "0");
    const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
    const month = months[d.getMonth()] || "—";
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "p. m." : "a. m.";
    hours = hours % 12 || 12;
    const hour = String(hours).padStart(2, "0");

    return `${day}-${month}-${year} · ${hour}:${minutes} ${ampm}`;
}

function Badge({ children, tone = "gray" }) {
    const tones = {
        gray: "border-[#24537a] bg-[#0f2d48] text-[#d9e8f5]",
        blue: "border-[#0ea5e9]/50 bg-[#0b3552] text-[#7dd3fc]",
        green: "border-[#10b981]/40 bg-[#0b3a36] text-[#7cebd0]",
        amber: "border-[#d4af37]/45 bg-[#3a3110] text-[#f6d76b]",
        red: "border-[#ef4444]/40 bg-[#38161c] text-[#fca5a5]",
        orange: "border-[#f97316]/40 bg-[#3b2413] text-[#fdba74]",
        purple: "border-[#8b5cf6]/40 bg-[#24163d] text-[#c4b5fd]",
    };
    return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold tracking-wide ${tones[tone] || tones.gray}`}>{children}</span>;
}

function SectionCard({ title, subtitle, children, right, className = "" }) {
    return (
        <div className={`orbis-section-card rounded-[24px] border border-[#0b4d78] bg-[#04192b] shadow-[0_12px_30px_rgba(0,0,0,0.18)] overflow-hidden ${className}`}>
            <div className="px-5 py-4 border-b border-[#0b4d78] flex items-start justify-between gap-3 bg-[#031425]">
                <div>
                    <h3 className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#d4af37]">{title}</h3>
                    {subtitle ? <p className="text-[11px] text-[#9fc4df] mt-1 max-w-3xl">{subtitle}</p> : null}
                </div>
                {right ? <div>{right}</div> : null}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

function Field({ label, children }) {
    return <div><label className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8fb9da] mb-1.5">{label}</label>{children}</div>;
}

const inputClass = () => "w-full rounded-xl border border-[#0b5f91] bg-[#0b2740] px-3 py-2 text-xs text-white placeholder:text-[#7ea8c7] focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37]";

function qualityTone(level) {
    if (level === "excelente") return "green";
    if (level === "buena") return "blue";
    if (level === "atencion") return "amber";
    return "red";
}

function ProgressBar({ value, tone = "blue" }) {
    const colors = {
        red: "linear-gradient(90deg, #ef4444 0%, #f87171 100%)",
        amber: "linear-gradient(90deg, #d4af37 0%, #facc15 100%)",
        blue: "linear-gradient(90deg, #0ea5e9 0%, #38bdf8 100%)",
        green: "linear-gradient(90deg, #10b981 0%, #34d399 100%)",
    };

    const width = Math.max(0, Math.min(100, Number(value || 0)));

    return (
        <div className="h-2.5 rounded-full bg-[#0b2740] overflow-hidden border border-[#0b4d78]">
            <div className="h-full rounded-full" style={{ width: `${width}%`, background: colors[tone] || colors.blue }} />
        </div>
    );
}

function OrbisFicha360Style() {
    return (
        <style>{`
            .orbis-ficha360{color:#f5fbff}
            .orbis-ficha360-wide{
                width:min(1500px, calc(100vw - 48px));
                margin-left:auto;
                margin-right:auto;
            }
            @media (max-width: 768px){
                .orbis-ficha360-wide{width:calc(100vw - 24px);}
            }
            .orbis-ficha360 .orbis-hero{background:linear-gradient(135deg,#03111f 0%,#031a2f 55%,#04223d 100%);border:1px solid rgba(11,93,140,.8);box-shadow:0 20px 45px rgba(0,0,0,.28)}
            .orbis-ficha360 .orbis-surface{background:#04192b;border:1px solid rgba(11,93,140,.82)}
            .orbis-ficha360 .orbis-muted{color:#9fc4df}
            .orbis-ficha360 .orbis-title{color:#fff}
            .orbis-ficha360 .orbis-cta-primary{background:#d4af37;color:#081a2b;border:1px solid #d4af37}
            .orbis-ficha360 .orbis-cta-primary:hover{background:#e8c457}
            .orbis-ficha360 .orbis-cta-secondary{background:#0b2740;color:#f5fbff;border:1px solid #0ea5e9}
            .orbis-ficha360 .orbis-cta-secondary:hover{background:#10324f}
            .orbis-ficha360 .orbis-cta-ghost{background:#08233d;color:#d9e8f5;border:1px solid #24537a}
            .orbis-ficha360 .orbis-cta-ghost:hover{background:#0d2d49}
            .orbis-ficha360 .orbis-kpi{border:1px solid rgba(11,93,140,.8);border-radius:20px;padding:14px 16px;background:#08233d}
            .orbis-ficha360 .orbis-kpi--red{background:#2b1117;border-color:rgba(239,68,68,.4)}
            .orbis-ficha360 .orbis-kpi--blue{background:#0b2740;border-color:rgba(14,165,233,.35)}
            .orbis-ficha360 .orbis-kpi--green{background:#0a332e;border-color:rgba(16,185,129,.35)}
            .orbis-ficha360 .orbis-table{min-width:1000px;width:100%;border-collapse:separate;border-spacing:0;overflow:hidden}
            .orbis-ficha360 .orbis-table thead th{position:sticky;top:0;z-index:1;background:#031425;color:#8fb9da;font-size:11px;letter-spacing:.14em;text-transform:uppercase;border-bottom:1px solid #0b4d78}
            .orbis-ficha360 .orbis-table tbody tr:nth-child(odd){background:#08233d}
            .orbis-ficha360 .orbis-table tbody tr:nth-child(even){background:#0b2740}
            .orbis-ficha360 .orbis-table tbody tr:hover{background:#10324f}
            .orbis-ficha360 .orbis-table tbody td{border-top:1px solid rgba(11,93,140,.55);color:#f5fbff}
            .orbis-ficha360 .orbis-subtext{color:#9fc4df}
            .orbis-ficha360 .orbis-card-mini{background:#0b2740;border:1px solid #0b4d78;border-radius:18px}
            .orbis-ficha360 .orbis-empty{background:#08233d;border:1px dashed #0b4d78;color:#9fc4df;border-radius:18px}
            .orbis-ficha360 .orbis-modal-panel{background:#04192b;border:1px solid #0b5f91;color:#fff;box-shadow:0 20px 60px rgba(0,0,0,.35)}
            .orbis-ficha360 .orbis-modal-header{background:#031425;border-bottom:1px solid #0b4d78}
            .orbis-ficha360 .orbis-divider{border-color:#0b4d78}
            .orbis-ficha360 input[type="checkbox"]{accent-color:#d4af37}

            .orbis-ficha360 select option{background:#061a2f;color:#fff}
            .orbis-ficha360 textarea{min-height:42px}
            .orbis-ficha360 .orbis-activity-scroll::-webkit-scrollbar{height:10px;width:10px}
            .orbis-ficha360 .orbis-activity-scroll::-webkit-scrollbar-track{background:#061a2f;border-radius:999px}
            .orbis-ficha360 .orbis-activity-scroll::-webkit-scrollbar-thumb{background:#2b638e;border-radius:999px}
            .orbis-ficha360 .orbis-activity-scroll::-webkit-scrollbar-thumb:hover{background:#d4af37}
            .orbis-ficha360 .orbis-actions-bar{background:#031425;border:1px solid #0b4d78;border-radius:22px}
            .orbis-ficha360 .orbis-inline-warning{background:#3a3110;border:1px solid rgba(212,175,55,.45);color:#f6d76b}
            .orbis-ficha360 .orbis-inline-info{background:#0b2740;border:1px solid rgba(14,165,233,.35);color:#d9f4ff}
            .orbis-ficha360 .orbis-contact-grid .orbis-card-mini:hover{border-color:#d4af37;background:#10324f}
            .orbis-ficha360 .orbis-table-action{border:1px solid #24537a;background:#0b2740;color:#f5fbff;border-radius:12px;padding:6px 10px;font-size:11px;font-weight:600}
            .orbis-ficha360 .orbis-table-action:hover{background:#10324f;border-color:#d4af37}
            .orbis-ficha360 .orbis-table-action-primary{border-color:#d4af37;background:#3a3110;color:#f6d76b}
            .orbis-ficha360 .orbis-table-action-success{border-color:rgba(16,185,129,.4);background:#0a332e;color:#7cebd0}
            .orbis-ficha360 .orbis-modal-panel input,
            .orbis-ficha360 .orbis-modal-panel select,
            .orbis-ficha360 .orbis-modal-panel textarea{background:#0b2740;color:#fff}

        `}</style>
    );
}

function computeOpportunityQuality(form, linkedContacts, remoteQuality) {
    const hasAccount = Boolean(form.account_id);
    const hasContact = Array.isArray(linkedContacts) && linkedContacts.length > 0;
    const hasPrimaryContact = hasContact && linkedContacts.some((c) => Number(c.is_primary) === 1);
    const hasNextAction = Boolean(form.next_action && form.next_action_date);
    const hasPilotValue = Number(form.pilot_value || 0) > 0;
    const hasMonthlyPotential = Number(form.monthly_potential || 0) > 0;
    const hasActivity = remoteQuality ? Number(remoteQuality.has_activity) === 1 : false;
    const hasFutureActivity = remoteQuality ? Number(remoteQuality.has_future_activity) === 1 : false;
    const hasOverdueActivity = remoteQuality ? Number(remoteQuality.has_overdue_activity) === 1 : false;

    let score = 0;
    score += hasAccount ? 10 : 0;
    score += hasContact ? 15 : 0;
    score += hasPrimaryContact ? 10 : 0;
    score += hasNextAction ? 20 : 0;
    score += hasPilotValue ? 15 : 0;
    score += hasMonthlyPotential ? 10 : 0;
    score += hasActivity ? 10 : 0;
    score += hasFutureActivity ? 10 : 0;
    score -= hasOverdueActivity ? 10 : 0;

    score = Math.max(0, Math.min(100, score));

    let level = "critica";
    if (score >= 85) level = "excelente";
    else if (score >= 65) level = "buena";
    else if (score >= 40) level = "atencion";

    const missing = [];
    if (!hasAccount) missing.push("Cuenta");
    if (!hasContact) missing.push("Contacto");
    if (hasContact && !hasPrimaryContact) missing.push("Contacto principal");
    if (!hasNextAction) missing.push("Próxima acción");
    if (!hasPilotValue) missing.push("Valor piloto");
    if (!hasMonthlyPotential) missing.push("Potencial mensual");
    if (!hasActivity) missing.push("Actividad");
    if (hasOverdueActivity) missing.push("Actividad vencida");

    const nextSteps = [];
    if (!hasContact) nextSteps.push("Agregar contacto clave");
    if (!hasNextAction) nextSteps.push("Definir próxima acción");
    if (!hasPilotValue || !hasMonthlyPotential) nextSteps.push("Capturar valor comercial");
    if (!hasActivity || hasOverdueActivity) nextSteps.push("Crear o atender actividad");

    return {
        score,
        level,
        missing,
        nextSteps,
        hasAccount,
        hasContact,
        hasPrimaryContact,
        hasNextAction,
        hasPilotValue,
        hasMonthlyPotential,
        hasActivity,
        hasFutureActivity,
        hasOverdueActivity,
    };
}


export default function OpportunityDetailPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isNew = !id || id === "new";

    const [form, setForm] = useState(EMPTY_FORM);
    const [accounts, setAccounts] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [pipelines, setPipelines] = useState([]);
    const [pipelineStages, setPipelineStages] = useState([]);
    const [productLineOptions, setProductLineOptions] = useState({
        campaigns: [],
        pipelines: [],
        stages: [],
        suggested_campaign_id: null,
        suggested_pipeline_id: null,
    });
    const [accountContacts, setAccountContacts] = useState([]);
    const [linkedContacts, setLinkedContacts] = useState([]);
    const [showNewContact, setShowNewContact] = useState(false);
    const [newContact, setNewContact] = useState({ full_name: "", email: "", mobile: "", phone: "", position: "", area: "", linkedin: "", role_label: "Contacto clave", role_in_opportunity: "Decisor / contacto clave", is_primary: true });
    const [selectedContactId, setSelectedContactId] = useState("");
    const [contactRole, setContactRole] = useState("Decisor / contacto clave");
    const [contactPrimary, setContactPrimary] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [toast, setToast] = useState("");
    const [commercialQuality, setCommercialQuality] = useState(null);
    const [showValueModal, setShowValueModal] = useState(false);
    const [showNextActionModal, setShowNextActionModal] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [showDemoModal, setShowDemoModal] = useState(false);
    const [valueDraft, setValueDraft] = useState({ pilot_value: "", monthly_potential: "", estimated_value: "" });
    const [nextActionDraft, setNextActionDraft] = useState({ next_action: "", next_action_date: "", status_comment: "" });
    const [activityDraft, setActivityDraft] = useState({
        title: "",
        type: "seguimiento_propuesta",
        channel: "whatsapp",
        start_datetime: "",
        objective: "",
        next_action: "",
        next_action_date: "",
    });
    const [demoDraft, setDemoDraft] = useState({
        start_datetime: "",
        duration_minutes: "60",
        channel: "meet",
        meeting_link: "",
        objective: "",
        notes: "",
        next_action: "",
        next_action_date: "",
    });
    const [opportunityActivities, setOpportunityActivities] = useState([]);
    const [activitySummary, setActivitySummary] = useState({});

    const selectedPipelineStages = useMemo(() => {
        if (!form.pipeline_id) return pipelineStages;
        return pipelineStages.filter((s) => String(s.pipeline_id) === String(form.pipeline_id));
    }, [pipelineStages, form.pipeline_id]);

    const campaignOptions = useMemo(() => {
        const filtered = campaigns.filter((c) => matchesProductCampaign(form.product_line, c));
        return filtered.length ? filtered : campaigns;
    }, [campaigns, form.product_line]);

    const pipelineOptions = useMemo(() => {
        const filtered = pipelines.filter((p) => matchesProductPipeline(form.product_line, p));
        return filtered.length ? filtered : pipelines;
    }, [pipelines, form.product_line]);

    async function loadProductLineOptions(productLine, applyDefaults = false) {
        try {
            if (!productLine) return null;

            const data = await safeJson(`${API_BASE}/crm_product_line_options.php?product_line=${encodeURIComponent(productLine)}`);

            const nextOptions = {
                campaigns: Array.isArray(data.campaigns) ? data.campaigns : [],
                pipelines: Array.isArray(data.pipelines) ? data.pipelines : [],
                stages: Array.isArray(data.stages) ? data.stages : [],
                suggested_campaign_id: data.suggested_campaign_id || null,
                suggested_pipeline_id: data.suggested_pipeline_id || null,
            };

            setProductLineOptions(nextOptions);

            if (applyDefaults) {
                setForm((prev) => {
                    const next = { ...prev };

                    if (nextOptions.suggested_campaign_id) {
                        next.campaign_id = String(nextOptions.suggested_campaign_id);
                    }

                    if (nextOptions.suggested_pipeline_id) {
                        next.pipeline_id = String(nextOptions.suggested_pipeline_id);
                        const firstStage = nextOptions.stages?.[0];
                        if (firstStage) {
                            next.pipeline_stage_id = String(firstStage.id);
                            next.probability = firstStage.probability_default ?? next.probability;
                        }
                    }

                    return next;
                });
            }

            return nextOptions;
        } catch (err) {
            console.error("loadProductLineOptions error:", err);
            return null;
        }
    }

    async function fetchCatalogs() {
        const [accData, indData, campData, pipeData, stageData] = await Promise.all([
            safeJson(`${API_BASE}/accounts_list.php`),
            safeJson(`${API_BASE}/industries_list.php`),
            safeJson(`${API_BASE}/campaigns_list.php?active_only=1`),
            safeJson(`${API_BASE}/pipelines_list.php?active_only=1`),
            safeJson(`${API_BASE}/pipeline_stages_list.php`),
        ]);
        const accs = asArray(accData), inds = asArray(indData), camps = asArray(campData), pipes = asArray(pipeData), stages = asArray(stageData);
        setAccounts(accs); setIndustries(inds); setCampaigns(camps); setPipelines(pipes); setPipelineStages(stages);
        return { campaigns: camps, pipelines: pipes, stages };
    }

    async function fetchContacts(accountId = form.account_id, opportunityId = form.id || id) {
        if (!accountId && !opportunityId) return;
        const data = await safeJson(`${API_BASE}/opportunity_contacts_list.php?account_id=${accountId || ""}&opportunity_id=${opportunityId || ""}`);
        setAccountContacts(Array.isArray(data.account_contacts) ? data.account_contacts : []);
        setLinkedContacts(Array.isArray(data.linked_contacts) ? data.linked_contacts : []);
    }

    async function fetchQualityForOpportunity(opportunityId = form.id || id) {
        if (!opportunityId || opportunityId === "new") {
            setCommercialQuality(null);
            return null;
        }

        try {
            const data = await safeJson(`${API_BASE}/crm_commercial_quality.php`);
            const items = Array.isArray(data.items) ? data.items : [];
            const item = items.find((row) => String(row.id) === String(opportunityId));
            setCommercialQuality(item || null);
            return item || null;
        } catch (err) {
            console.error("fetchQualityForOpportunity error:", err);
            setCommercialQuality(null);
            return null;
        }
    }

    async function fetchOpportunityActivities(opportunityId = form.id || id) {
        if (!opportunityId || opportunityId === "new") {
            setOpportunityActivities([]);
            setActivitySummary({});
            return null;
        }

        try {
            const data = await safeJson(`${API_BASE}/opportunity_activities_list.php?opportunity_id=${opportunityId}`);
            setOpportunityActivities(Array.isArray(data.activities) ? data.activities : []);
            setActivitySummary(data.summary || {});
            return data;
        } catch (err) {
            console.error("fetchOpportunityActivities error:", err);
            setOpportunityActivities([]);
            setActivitySummary({});
            return null;
        }
    }

    async function completeActivityQuick(activityId) {
        if (!activityId) return;
        try {
            setSaving(true);
            setError("");
            setToast("");

            await safeJson(`${API_BASE}/activity_quick_complete.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    id: Number(activityId),
                    result: "Actividad realizada desde Ficha Comercial 360.",
                }),
            });

            setToast("Actividad marcada como realizada.");
            await fetchOpportunityActivities(form.id || id);
            await fetchQualityForOpportunity(form.id || id);
        } catch (err) {
            setError(err.message || "No se pudo marcar la actividad como realizada.");
        } finally {
            setSaving(false);
        }
    }

    function openActivityFromHistory(activity) {
        setActivityDraft({
            title: activity?.next_action || activity?.title || `Seguimiento comercial - ${form.name || "Oportunidad"}`,
            type: activity?.type || "seguimiento_propuesta",
            channel: activity?.channel || "whatsapp",
            start_datetime: activity?.next_action_date ? toDateTimeLocal(activity.next_action_date) : defaultDateTimeLocal(30),
            objective: activity?.next_action ? `Dar seguimiento a: ${activity.next_action}` : "Dar siguiente seguimiento comercial.",
            next_action: "",
            next_action_date: "",
        });
        setShowActivityModal(true);
    }


    async function fetchOpportunity(catalogs) {
        if (isNew) {
            const camp = catalogs.campaigns.find((c) => c.code === "ORBIS-PMO-LANZAMIENTO") || catalogs.campaigns[0];
            const pipe = catalogs.pipelines.find((p) => p.code === "VENTA-ORBIS-PMO") || catalogs.pipelines.find((p) => Number(p.is_default) === 1) || catalogs.pipelines[0];
            const stage = catalogs.stages.filter((s) => String(s.pipeline_id) === String(pipe?.id)).sort((a, b) => Number(a.stage_order || 0) - Number(b.stage_order || 0))[0];
            setForm({ ...EMPTY_FORM, campaign_id: camp?.id ? String(camp.id) : "", pipeline_id: pipe?.id ? String(pipe.id) : "", pipeline_stage_id: stage?.id ? String(stage.id) : "", probability: stage?.probability_default ?? "" });
            return;
        }

        const data = await safeJson(`${API_BASE}/opportunity_detail.php?id=${id}`);
        const o = data.opportunity;
        setForm({
            id: o.id || null, name: o.name || "", code: o.code || "", account_id: o.account_id ? String(o.account_id) : "",
            description: o.description || "", notes: o.notes || "", campaign_id: o.campaign_id ? String(o.campaign_id) : "",
            pipeline_id: o.pipeline_id ? String(o.pipeline_id) : "", pipeline_stage_id: o.pipeline_stage_id ? String(o.pipeline_stage_id) : "",
            product_line: o.product_line || "ORBIS PMO", lead_source: o.lead_source || o.source || "", probability: o.probability ?? "",
            expected_close_date: toDate(o.expected_close_date), estimated_value: o.estimated_value ?? "", currency: o.currency || "MXN",
            industry_id: o.industry_id ? String(o.industry_id) : "", pain_point: o.pain_point || "", current_tool: o.current_tool || "",
            active_projects_count: o.active_projects_count ?? "", estimated_users: o.estimated_users ?? "", pilot_package: o.pilot_package || "",
            pilot_value: o.pilot_value ?? "", monthly_potential: o.monthly_potential ?? "", decision_maker: o.decision_maker || "",
            urgency: o.urgency || "", next_action: o.next_action || "", next_action_date: toDateTimeLocal(o.next_action_date),
            status_comment: o.status_comment || "",
        });
        await fetchContacts(o.account_id, o.id);
        await fetchQualityForOpportunity(o.id);
        await fetchOpportunityActivities(o.id);
    }

    useEffect(() => {
        (async () => {
            try {
                setLoading(true); setError("");
                const catalogs = await fetchCatalogs();
                await fetchOpportunity(catalogs);
            } catch (err) {
                setError(err.message || "No se pudo cargar la oportunidad.");
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);


    useEffect(() => {
        if (form.product_line) {
            loadProductLineOptions(form.product_line, false);
        }
    }, [form.product_line]);

    function handleChange(e) {
        const { name, value } = e.target;
        setForm((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "pipeline_id") {
                const firstStage = pipelineStages.filter((s) => String(s.pipeline_id) === String(value)).sort((a, b) => Number(a.stage_order || 0) - Number(b.stage_order || 0))[0];
                next.pipeline_stage_id = firstStage?.id ? String(firstStage.id) : "";
                next.probability = firstStage?.probability_default ?? "";
            }
            if (name === "pipeline_stage_id") {
                const stage = pipelineStages.find((s) => String(s.id) === String(value));
                if (stage?.probability_default !== null && stage?.probability_default !== undefined) next.probability = String(stage.probability_default);
            }
            if (name === "product_line") {
                const nextCampaign = campaigns.find((c) => matchesProductCampaign(value, c));
                const nextPipeline = pipelines.find((p) => matchesProductPipeline(value, p));

                if (nextCampaign) next.campaign_id = String(nextCampaign.id);

                if (nextPipeline) {
                    next.pipeline_id = String(nextPipeline.id);
                    const firstStage = pipelineStages
                        .filter((s) => String(s.pipeline_id) === String(nextPipeline.id))
                        .sort((a, b) => Number(a.stage_order || 0) - Number(b.stage_order || 0))[0];

                    next.pipeline_stage_id = firstStage?.id ? String(firstStage.id) : "";
                    next.probability = firstStage?.probability_default ?? next.probability;
                }
            }

            if (name === "pilot_package") {
                if (value === "base" && !next.pilot_value) next.pilot_value = "35000";
                if (value === "ejecutivo" && !next.pilot_value) next.pilot_value = "65000";
                if (value === "institucional" && !next.pilot_value) next.pilot_value = "120000";
            }
            return next;
        });
    }

    async function handleSave(e) {
        e.preventDefault();
        setError(""); setToast("");
        if (!form.name.trim()) return setError("El nombre de la oportunidad es obligatorio.");
        if (!form.account_id) return setError("La cuenta es obligatoria.");

        try {
            setSaving(true);
            const payload = {
                ...form, id: form.id || id,
                account_id: form.account_id ? Number(form.account_id) : null,
                campaign_id: form.campaign_id ? Number(form.campaign_id) : null,
                pipeline_id: form.pipeline_id ? Number(form.pipeline_id) : null,
                pipeline_stage_id: form.pipeline_stage_id ? Number(form.pipeline_stage_id) : null,
                industry_id: form.industry_id ? Number(form.industry_id) : null,
                probability: form.probability !== "" ? Number(form.probability) : null,
                estimated_value: form.estimated_value !== "" ? Number(form.estimated_value) : null,
                active_projects_count: form.active_projects_count !== "" ? Number(form.active_projects_count) : null,
                estimated_users: form.estimated_users !== "" ? Number(form.estimated_users) : null,
                pilot_value: form.pilot_value !== "" ? Number(form.pilot_value) : null,
                monthly_potential: form.monthly_potential !== "" ? Number(form.monthly_potential) : null,
                next_action_date: normalizeDateTimeForApi(form.next_action_date),
            };
            const endpoint = isNew ? `${API_BASE}/opportunity_create.php` : `${API_BASE}/opportunity_update.php`;
            const data = await safeJson(endpoint, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
            if (isNew && data.id) {
                setToast("Oportunidad creada correctamente.");
                navigate(`/opportunities/${data.id}/edit`, { replace: true });
                return;
            }
            setToast("Oportunidad actualizada correctamente.");
            await fetchContacts(form.account_id, form.id || id);
            await fetchQualityForOpportunity(form.id || id);
            await fetchOpportunityActivities(form.id || id);
        } catch (err) {
            setError(err.message || "No se pudo guardar la oportunidad.");
        } finally {
            setSaving(false);
        }
    }

    async function linkSelectedContact() {
        if (!selectedContactId) return setError("Selecciona un contacto para vincular.");
        if (isNew) return setError("Primero guarda la oportunidad para vincular contactos.");
        try {
            setSaving(true); setError("");
            await safeJson(`${API_BASE}/opportunity_contact_link.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ opportunity_id: Number(id), contact_id: Number(selectedContactId), role_label: contactRole, role_in_opportunity: contactRole, is_primary: contactPrimary }),
            });
            setSelectedContactId(""); setToast("Contacto vinculado a la oportunidad.");
            await fetchContacts(form.account_id, id);
            await fetchQualityForOpportunity(id);
            await fetchQualityForOpportunity(id);
        } catch (err) {
            setError(err.message || "No se pudo vincular el contacto.");
        } finally {
            setSaving(false);
        }
    }

    async function createContact() {
        if (isNew) return setError("Primero guarda la oportunidad para crear y vincular contactos.");
        if (!newContact.full_name.trim()) return setError("El nombre del contacto es obligatorio.");
        try {
            setSaving(true); setError("");
            await safeJson(`${API_BASE}/contact_create_crm.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ ...newContact, account_id: Number(form.account_id), opportunity_id: Number(id) }),
            });
            setNewContact({ full_name: "", email: "", mobile: "", phone: "", position: "", area: "", linkedin: "", role_label: "Contacto clave", role_in_opportunity: "Decisor / contacto clave", is_primary: true });
            setShowNewContact(false); setToast("Contacto creado y vinculado correctamente.");
            await fetchContacts(form.account_id, id);
        } catch (err) {
            setError(err.message || "No se pudo crear el contacto.");
        } finally {
            setSaving(false);
        }
    }

    function openValueModal() {
        setValueDraft({
            pilot_value: form.pilot_value || "",
            monthly_potential: form.monthly_potential || "",
            estimated_value: form.estimated_value || "",
        });
        setShowValueModal(true);
    }

    function openNextActionModal() {
        setNextActionDraft({
            next_action: form.next_action || "",
            next_action_date: form.next_action_date || defaultDateTimeLocal(30),
            status_comment: form.status_comment || "",
        });
        setShowNextActionModal(true);
    }

    function openActivityModal() {
        const title = form.next_action || `Seguimiento comercial - ${form.name || "Oportunidad"}`;
        const start = form.next_action_date || defaultDateTimeLocal(30);
        setActivityDraft({
            title,
            type: "seguimiento_propuesta",
            channel: "whatsapp",
            start_datetime: start,
            objective: form.pain_point ? `Dar seguimiento a: ${form.pain_point}` : "Dar seguimiento comercial a la oportunidad.",
            next_action: "",
            next_action_date: "",
        });
        setShowActivityModal(true);
    }

    function openScheduleDemoModal() {
        if (isNew) {
            setError("Primero guarda la oportunidad para agendar demo.");
            return;
        }

        const start = form.next_action_date || defaultDateTimeLocal(60);

        setDemoDraft({
            start_datetime: start,
            duration_minutes: "60",
            channel: "meet",
            meeting_link: "",
            objective: `Mostrar ORBIS PMO en acción para ${form.account_id ? "la cuenta seleccionada" : "el prospecto"} y validar si la plataforma resuelve el dolor principal: ${form.pain_point || "control, trazabilidad y seguimiento de proyectos"}.`,
            notes: "Demo guiada desde Ficha Comercial 360. Preparar lectura de Dashboard Ejecutivo, Ficha 360, evidencias, tareas, riesgos y ruta de decisión.",
            next_action: "Dar seguimiento posterior a demo y definir propuesta piloto.",
            next_action_date: "",
        });

        setShowDemoModal(true);
    }

    async function scheduleGuidedDemo(e) {
        e?.preventDefault?.();

        if (isNew) return setError("Primero guarda la oportunidad para agendar demo.");
        if (!demoDraft.start_datetime) return setError("La fecha y hora de demo es obligatoria.");
        if (!demoDraft.channel) return setError("El canal de demo es obligatorio.");

        try {
            setSaving(true);
            setError("");
            setToast("");

            const data = await safeJson(`${API_BASE}/orbis_demo_schedule.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    opportunity_id: Number(form.id || id),
                    start_datetime: normalizeDateTimeForApi(demoDraft.start_datetime),
                    duration_minutes: Number(demoDraft.duration_minutes || 60),
                    channel: demoDraft.channel,
                    meeting_link: demoDraft.meeting_link,
                    objective: demoDraft.objective,
                    notes: demoDraft.notes,
                    next_action: demoDraft.next_action,
                    next_action_date: normalizeDateTimeForApi(demoDraft.next_action_date),
                    created_by: Number(localStorage.getItem("user_id")) || Number(localStorage.getItem("id")) || null,
                }),
            });

            const updated = data?.data || {};

            setForm((prev) => ({
                ...prev,
                pipeline_stage_id: updated.pipeline_stage_id ? String(updated.pipeline_stage_id) : prev.pipeline_stage_id,
                probability: updated.probability ?? prev.probability,
                next_action: updated.next_action || prev.next_action,
                next_action_date: updated.next_action_date ? toDateTimeLocal(updated.next_action_date) : prev.next_action_date,
                status_comment: updated.status_comment || prev.status_comment,
            }));

            setShowDemoModal(false);
            setToast("Demo guiada agendada correctamente.");
            await fetchOpportunityActivities(form.id || id);
            await fetchQualityForOpportunity(form.id || id);
        } catch (err) {
            setError(err.message || "No se pudo agendar la demo guiada.");
        } finally {
            setSaving(false);
        }
    }

    async function saveCommercialValueQuick(e) {
        e?.preventDefault?.();
        if (isNew) return setError("Primero guarda la oportunidad para capturar valor.");
        try {
            setSaving(true);
            setError("");
            setToast("");

            await safeJson(`${API_BASE}/opportunity_commercial_quick_update.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    opportunity_id: Number(form.id || id),
                    pilot_value: valueDraft.pilot_value !== "" ? Number(valueDraft.pilot_value) : null,
                    monthly_potential: valueDraft.monthly_potential !== "" ? Number(valueDraft.monthly_potential) : null,
                    estimated_value: valueDraft.estimated_value !== "" ? Number(valueDraft.estimated_value) : null,
                }),
            });

            setForm((prev) => ({
                ...prev,
                pilot_value: valueDraft.pilot_value,
                monthly_potential: valueDraft.monthly_potential,
                estimated_value: valueDraft.estimated_value,
            }));

            setShowValueModal(false);
            setToast("Valor comercial actualizado.");
            await fetchQualityForOpportunity(form.id || id);
        } catch (err) {
            setError(err.message || "No se pudo actualizar el valor comercial.");
        } finally {
            setSaving(false);
        }
    }

    async function saveNextActionQuick(e) {
        e?.preventDefault?.();
        if (isNew) return setError("Primero guarda la oportunidad para crear próxima acción.");
        if (!nextActionDraft.next_action) return setError("La próxima acción es obligatoria.");
        if (!nextActionDraft.next_action_date) return setError("La fecha de próxima acción es obligatoria.");
        try {
            setSaving(true);
            setError("");
            setToast("");

            await safeJson(`${API_BASE}/opportunity_commercial_quick_update.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    opportunity_id: Number(form.id || id),
                    next_action: nextActionDraft.next_action,
                    next_action_date: normalizeDateTimeForApi(nextActionDraft.next_action_date),
                    status_comment: nextActionDraft.status_comment,
                }),
            });

            setForm((prev) => ({
                ...prev,
                next_action: nextActionDraft.next_action,
                next_action_date: nextActionDraft.next_action_date,
                status_comment: nextActionDraft.status_comment,
            }));

            setShowNextActionModal(false);
            setToast("Próxima acción actualizada.");
            await fetchQualityForOpportunity(form.id || id);
        } catch (err) {
            setError(err.message || "No se pudo actualizar la próxima acción.");
        } finally {
            setSaving(false);
        }
    }

    async function createActivityQuick(e) {
        e?.preventDefault?.();
        if (isNew) return setError("Primero guarda la oportunidad para crear actividad.");
        if (!activityDraft.title) return setError("El título de la actividad es obligatorio.");
        if (!activityDraft.start_datetime) return setError("La fecha de actividad es obligatoria.");
        try {
            setSaving(true);
            setError("");
            setToast("");

            await safeJson(`${API_BASE}/activity_create.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    account_id: form.account_id ? Number(form.account_id) : null,
                    opportunity_id: Number(form.id || id),
                    type: activityDraft.type,
                    channel: activityDraft.channel,
                    title: activityDraft.title,
                    objective: activityDraft.objective,
                    status: "programada",
                    start_datetime: normalizeDateTimeForApi(activityDraft.start_datetime),
                    next_action: activityDraft.next_action || null,
                    next_action_date: normalizeDateTimeForApi(activityDraft.next_action_date),
                    alerts: [],
                }),
            });

            setShowActivityModal(false);
            setToast("Actividad comercial creada.");
            await fetchOpportunityActivities(form.id || id);
            await fetchQualityForOpportunity(form.id || id);
        } catch (err) {
            setError(err.message || "No se pudo crear la actividad.");
        } finally {
            setSaving(false);
        }
    }

    const selectedStage = pipelineStages.find((s) => String(s.id) === String(form.pipeline_stage_id));

    const quality = useMemo(() => computeOpportunityQuality(form, linkedContacts, commercialQuality), [form, linkedContacts, commercialQuality]);

    function scrollToQualitySection(sectionId) {
        setTimeout(() => {
            const el = document.getElementById(sectionId);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
    }

    function goToContactKeySection() {
        setShowNewContact(true);
        scrollToQualitySection("commercial-contact-section");
    }

    if (loading) {
        return <MainLayout><div className="min-h-screen bg-[#041426] px-4 py-6 md:px-6"><div className="orbis-ficha360-wide rounded-[24px] border border-[#0b4d78] bg-[#04192b] px-5 py-6 text-sm text-[#9fc4df]">Cargando ficha comercial...</div></div></MainLayout>;
    }

    return (
        <MainLayout>
            <OrbisFicha360Style />
            <div className="orbis-ficha360 min-h-screen bg-[#041426] px-3 py-6 md:px-6 md:py-8"><div className="orbis-ficha360-wide space-y-5">
                <div className="orbis-hero rounded-[28px] px-6 py-6 md:px-7 md:py-7 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                    <div>
                        <button type="button" onClick={() => navigate("/opportunities")} className="text-[11px] text-[#8fd8ff] hover:text-[#d4af37] hover:underline mb-3 inline-flex items-center gap-1">← Regresar a oportunidades</button>
                        <div className="space-y-1">
                            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">Ficha Comercial 360</h2>
                            <p className="text-sm md:text-base text-[#d7e8f6] max-w-3xl leading-relaxed">Campaña, pipeline, contacto clave, calificación ORBIS PMO, siguiente acción comercial y actividades comerciales.</p>
                            <div className="pt-1">
                                <span className="text-[11px] uppercase tracking-[0.22em] text-[#d4af37]">Oportunidad actual</span>
                                <div className="text-xl md:text-2xl font-bold text-white">{isNew ? "Nueva oportunidad" : form.name || "Oportunidad"}</div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Badge tone="blue">{selectedStage?.name || "Sin etapa"}</Badge>
                        <Badge tone={form.urgency === "critica" ? "red" : form.urgency === "alta" ? "orange" : "gray"}>{form.urgency || "Sin urgencia"}</Badge>
                        <Badge tone="amber">Piloto: {formatCurrency(form.pilot_value, form.currency)}</Badge>
                        <Badge tone="green">Mensual: {formatCurrency(form.monthly_potential, form.currency)}</Badge>
                        {!isNew ? (
                            <button
                                type="button"
                                onClick={openActivityModal}
                                className="orbis-cta-primary px-3 py-1.5 rounded-full text-[10px] font-semibold"
                            >
                                + Actividad
                            </button>
                        ) : null}
                    </div>
                </div>

                {!isNew ? (
                    <div className="orbis-actions-bar px-4 py-3 flex flex-wrap items-center gap-2 text-[11px]">
                        <span className="text-[#8fb9da] font-semibold uppercase tracking-[0.16em]">Ruta comercial</span>
                        <Badge tone="blue">Lead</Badge>
                        <span className="text-[#24537a]">→</span>
                        <Badge tone={selectedStage?.stage_order >= 4 ? "green" : "gray"}>Demo</Badge>
                        <span className="text-[#24537a]">→</span>
                        <Badge tone={selectedStage?.stage_order >= 6 ? "amber" : "gray"}>Propuesta</Badge>
                        <span className="text-[#24537a]">→</span>
                        <Badge tone={selectedStage?.stage_order >= 8 ? "green" : "gray"}>Piloto</Badge>
                        <span className="text-[#24537a]">→</span>
                        <Badge tone={selectedStage?.stage_order >= 10 ? "green" : "gray"}>Continuidad</Badge>
                    </div>
                ) : null}

                {toast ? <div className="text-xs rounded-xl px-4 py-3 border border-[#10b981]/40 bg-[#0a332e] text-[#86efac]">{toast}</div> : null}
                {error ? <div className="text-xs rounded-xl px-4 py-3 border border-[#ef4444]/40 bg-[#2b1117] text-[#fca5a5]">{error}</div> : null}

                <SectionCard
                    title="Checklist de calidad comercial"
                    subtitle="Normaliza la captura antes de avanzar la oportunidad: contacto, próxima acción, actividad y valor comercial."
                    right={<Badge tone={qualityTone(quality.level)}>{quality.score}/100 · {quality.level}</Badge>}
                >
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        <div className="lg:col-span-1">
                            <div className="text-[11px] text-[#9fc4df] mb-1">Score de oportunidad</div>
                            <div className="text-3xl font-semibold text-white">{quality.score}/100</div>
                            <div className="mt-2">
                                <ProgressBar value={quality.score} tone={qualityTone(quality.level)} />
                            </div>
                            <div className="flex justify-between text-[10px] text-[#8fb9da] mt-1">
                                <span>Crítica</span>
                                <span>Excelente</span>
                            </div>
                        </div>

                        <div className="lg:col-span-2">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#d4af37] mb-2">Estado de captura</div>
                            <div className="flex flex-wrap gap-1.5">
                                <Badge tone={quality.hasAccount ? "green" : "red"}>Cuenta</Badge>
                                <Badge tone={quality.hasContact ? "green" : "red"}>Contacto</Badge>
                                <Badge tone={quality.hasPrimaryContact ? "green" : "amber"}>Contacto principal</Badge>
                                <Badge tone={quality.hasNextAction ? "green" : "red"}>Próxima acción</Badge>
                                <Badge tone={quality.hasPilotValue ? "green" : "amber"}>Valor piloto</Badge>
                                <Badge tone={quality.hasMonthlyPotential ? "green" : "amber"}>Potencial mensual</Badge>
                                <Badge tone={quality.hasActivity ? "green" : "red"}>Actividad</Badge>
                                <Badge tone={quality.hasFutureActivity ? "green" : quality.hasOverdueActivity ? "red" : "gray"}>{quality.hasFutureActivity ? "Actividad futura" : quality.hasOverdueActivity ? "Actividad vencida" : "Sin actividad futura"}</Badge>
                            </div>

                            <div className="mt-3 text-[11px] text-[#9fc4df]">
                                {quality.missing.length ? (
                                    <>Faltantes: {quality.missing.join(", ")}.</>
                                ) : (
                                    <>La oportunidad cuenta con la captura mínima recomendada.</>
                                )}
                            </div>
                        </div>

                        <div className="lg:col-span-1">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#d4af37] mb-2">Acciones rápidas</div>
                            <div className="orbis-actions-bar p-3 flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={openValueModal}
                                    className="orbis-cta-primary px-3 py-2 rounded-xl text-[11px] font-semibold"
                                >
                                    Capturar valor
                                </button>
                                <button
                                    type="button"
                                    onClick={openNextActionModal}
                                    className="orbis-table-action orbis-table-action-primary"
                                >
                                    Próxima acción
                                </button>
                                {!isNew ? (
                                    <button
                                        type="button"
                                        onClick={openScheduleDemoModal}
                                        className="orbis-table-action orbis-table-action-primary"
                                        title="Agendar una demo guiada de ORBIS PMO y mover la oportunidad a Reunión agendada"
                                    >
                                        Agendar demo
                                    </button>
                                ) : null}
                                {!isNew ? (
                                    <button
                                        type="button"
                                        onClick={openActivityModal}
                                        className="orbis-table-action"
                                    >
                                        Crear actividad
                                    </button>
                                ) : null}
                                {!isNew ? (
                                    <button
                                        type="button"
                                        onClick={goToContactKeySection}
                                        className="orbis-table-action orbis-table-action-primary"
                                        title="Ir al bloque de contacto clave"
                                    >
                                        Contacto clave
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </SectionCard>

                <form onSubmit={handleSave} className="space-y-4">
                    <SectionCard title="Lectura comercial" subtitle="Datos centrales de la oportunidad, campaña, pipeline y lectura ejecutiva.">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                            <Field label="Cuenta *"><select name="account_id" value={form.account_id} onChange={handleChange} className={inputClass()}><option value="">— Seleccionar cuenta —</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}{a.alias ? ` · ${a.alias}` : ""}</option>)}</select></Field>
                            <Field label="Nombre de oportunidad *"><input name="name" value={form.name} onChange={handleChange} className={inputClass()} /></Field>
                            <Field label="Código"><input name="code" value={form.code} onChange={handleChange} className={inputClass()} /></Field>
                            <Field label="Producto / línea"><select name="product_line" value={form.product_line} onChange={handleChange} className={inputClass()}>{PRODUCT_LINES.map((line) => <option key={line} value={line}>{line}</option>)}</select></Field>
                            <Field label="Campaña"><select name="campaign_id" value={form.campaign_id} onChange={handleChange} className={inputClass()}><option value="">— Sin campaña —</option>{campaignOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
                            <Field label="Pipeline"><select name="pipeline_id" value={form.pipeline_id} onChange={handleChange} className={inputClass()}><option value="">— Sin pipeline —</option>{pipelineOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
                            <Field label="Etapa"><select name="pipeline_stage_id" value={form.pipeline_stage_id} onChange={handleChange} className={inputClass()}><option value="">— Sin etapa —</option>{selectedPipelineStages.map((s) => <option key={s.id} value={s.id}>{s.stage_order}. {s.name}</option>)}</select></Field>
                            <Field label="Probabilidad %"><input type="number" min="0" max="100" name="probability" value={form.probability} onChange={handleChange} className={inputClass()} /></Field>
                            <Field label="Fuente del lead"><select name="lead_source" value={form.lead_source} onChange={handleChange} className={inputClass()}>{SOURCE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                            <Field label="Urgencia"><select name="urgency" value={form.urgency} onChange={handleChange} className={inputClass()}>{URGENCY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                            <Field label="Fecha objetivo de cierre"><input type="date" name="expected_close_date" value={form.expected_close_date} onChange={handleChange} className={inputClass()} /></Field>
                            <Field label="Sector estratégico"><select name="industry_id" value={form.industry_id} onChange={handleChange} className={inputClass()}><option value="">— Sin especificar —</option>{industries.map((i) => <option key={i.id} value={i.id}>{i.name}{i.code ? ` (${i.code})` : ""}</option>)}</select></Field>
                        </div>
                    </SectionCard>

                    <div id="commercial-value-section"><SectionCard title="Calificación comercial ORBIS PMO" subtitle="Datos para evaluar piloto, continuidad y enfoque de la demo.">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                            <div className="md:col-span-2"><Field label="Área a fortalecer"><textarea name="pain_point" value={form.pain_point} onChange={handleChange} rows={3} className={`${inputClass()} resize-none`} /></Field></div>
                            <div className="md:col-span-2"><Field label="Descripción / contexto"><textarea name="description" value={form.description} onChange={handleChange} rows={3} className={`${inputClass()} resize-none`} /></Field></div>
                            <Field label="Herramienta actual"><input name="current_tool" value={form.current_tool} onChange={handleChange} className={inputClass()} /></Field>
                            <Field label="Proyectos activos"><input type="number" min="0" name="active_projects_count" value={form.active_projects_count} onChange={handleChange} className={inputClass()} /></Field>
                            <Field label="Usuarios estimados"><input type="number" min="0" name="estimated_users" value={form.estimated_users} onChange={handleChange} className={inputClass()} /></Field>
                            <Field label="Decisor / contacto clave textual"><input name="decision_maker" value={form.decision_maker} onChange={handleChange} className={inputClass()} /></Field>
                            <Field label="Paquete piloto"><select name="pilot_package" value={form.pilot_package} onChange={handleChange} className={inputClass()}>{PILOT_PACKAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                            <Field label="Valor piloto"><input type="number" min="0" name="pilot_value" value={form.pilot_value} onChange={handleChange} className={inputClass()} /></Field>
                            <Field label="Potencial mensual"><input type="number" min="0" name="monthly_potential" value={form.monthly_potential} onChange={handleChange} className={inputClass()} /></Field>
                            <Field label="Valor estimado general"><input type="number" min="0" name="estimated_value" value={form.estimated_value} onChange={handleChange} className={inputClass()} /></Field>
                        </div>
                    </SectionCard></div>

                    <div id="commercial-next-action-section"><SectionCard title="Siguiente acción comercial" subtitle="Siguiente paso para mantener viva la oportunidad comercial.">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                            <div className="md:col-span-2"><Field label="Siguiente acción comercial"><input name="next_action" value={form.next_action} onChange={handleChange} className={inputClass()} /></Field></div>
                            <Field label="Fecha siguiente acción comercial"><input type="datetime-local" name="next_action_date" value={form.next_action_date} onChange={handleChange} className={inputClass()} /></Field>
                            <div className="md:col-span-3"><Field label="Comentario comercial"><textarea name="status_comment" value={form.status_comment} onChange={handleChange} rows={2} className={`${inputClass()} resize-none`} /></Field></div>
                            <div className="md:col-span-3"><Field label="Notas internas comerciales"><textarea name="notes" value={form.notes} onChange={handleChange} rows={3} className={`${inputClass()} resize-none`} /></Field></div>
                        </div>
                    </SectionCard></div>

                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => navigate("/opportunities")} className="orbis-table-action">Regresar</button>
                        <button type="submit" disabled={saving} className="orbis-table-action orbis-table-action-primary disabled:opacity-60">{saving ? "Guardando..." : isNew ? "Crear oportunidad" : "Guardar cambios"}</button>
                    </div>
                </form>

                <SectionCard
                    title="Actividades de la oportunidad"
                    subtitle="Historial, próximas acciones y actividades vencidas vinculadas a esta oportunidad."
                    right={!isNew ? (
                        <button
                            type="button"
                            onClick={openActivityModal}
                            className="orbis-table-action orbis-table-action-primary"
                        >
                            + Nueva actividad
                        </button>
                    ) : null}
                >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4 text-xs">
                        <div className="orbis-kpi p-4">
                            <div className="text-[11px] text-[#9fc4df]">Total</div>
                            <div className="text-2xl font-semibold text-white">{activitySummary.total || 0}</div>
                        </div>
                        <div className="orbis-kpi orbis-kpi--red p-4">
                            <div className="text-[11px] text-[#fca5a5]">Vencidas</div>
                            <div className="text-2xl font-semibold text-[#fecaca]">{activitySummary.overdue || 0}</div>
                        </div>
                        <div className="orbis-kpi orbis-kpi--blue p-4">
                            <div className="text-[11px] text-[#7dd3fc]">Próximas</div>
                            <div className="text-2xl font-semibold text-[#bae6fd]">{activitySummary.upcoming || 0}</div>
                        </div>
                        <div className="orbis-kpi orbis-kpi--green p-4">
                            <div className="text-[11px] text-[#7cebd0]">Realizadas</div>
                            <div className="text-2xl font-semibold text-[#bbf7d0]">{activitySummary.completed || 0}</div>
                        </div>
                    </div>

                    {opportunityActivities.length === 0 ? (
                        <div className="orbis-empty p-4 text-xs">
                            Esta oportunidad aún no tiene actividades vinculadas.
                            {!isNew ? (
                                <button type="button" onClick={openActivityModal} className="ml-2 text-cani-primary hover:underline">
                                    Crear primera actividad
                                </button>
                            ) : null}
                        </div>
                    ) : (
                        <div className="orbis-activity-scroll overflow-auto rounded-[20px] border border-[#0b4d78] bg-[#041427] max-h-[520px]">
                            <table className="orbis-table text-xs">
                                <thead>
                                    <tr className="text-left font-semibold">
                                        <th className="px-3 py-2">Fecha</th>
                                        <th className="px-3 py-2">Actividad</th>
                                        <th className="px-3 py-2">Tipo / canal</th>
                                        <th className="px-3 py-2">Estado</th>
                                        <th className="px-3 py-2">Próxima acción</th>
                                        <th className="px-3 py-2 text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {opportunityActivities.map((act, idx) => (
                                        <tr key={act.id} className="">
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <div>{formatDateTime(act.start_datetime)}</div>
                                                {Number(act.is_overdue) === 1 ? <Badge tone="red">Vencida</Badge> : null}
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="font-semibold text-white">{act.title || "Actividad comercial"}</div>
                                                <div className="text-[11px] text-[#9fc4df]">{act.objective || "Sin objetivo registrado."}</div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div>{act.type || "Sin tipo"}</div>
                                                <div className="text-[11px] text-[#9fc4df]">{act.channel || "Sin canal"}</div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <Badge tone={act.status === "realizada" ? "green" : act.status === "cancelada" ? "red" : Number(act.is_overdue) === 1 ? "red" : "blue"}>
                                                    {act.status || "programada"}
                                                </Badge>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div>{act.next_action || "Sin siguiente acción"}</div>
                                                <div className="text-[11px] text-[#9fc4df]">{formatDateTime(act.next_action_date)}</div>
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <div className="flex flex-wrap justify-end gap-1">
                                                    {act.status !== "realizada" && act.status !== "cancelada" ? (
                                                        <button
                                                            type="button"
                                                            disabled={saving}
                                                            onClick={() => completeActivityQuick(act.id)}
                                                            className="orbis-table-action orbis-table-action-success disabled:opacity-60"
                                                        >
                                                            Realizada
                                                        </button>
                                                    ) : null}
                                                    <button
                                                        type="button"
                                                        onClick={() => openActivityFromHistory(act)}
                                                        className="orbis-table-action orbis-table-action-primary"
                                                    >
                                                        Siguiente
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(`/agenda?opportunity_id=${form.id || id}&account_id=${form.account_id || ""}`)}
                                                        className="orbis-table-action"
                                                    >
                                                        Agenda
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </SectionCard>

                <div id="commercial-contact-section">
                    <SectionCard title="Contacto clave" subtitle={isNew ? "Guarda primero la oportunidad para vincular contactos." : "Selecciona un contacto de la cuenta o crea uno nuevo vinculado a la oportunidad."} right={!isNew ? <button type="button" onClick={() => setShowNewContact((v) => !v)} className="orbis-table-action">{showNewContact ? "Cerrar" : "+ Nuevo contacto"}</button> : null}>
                        {linkedContacts.length > 0 ? (
                            <div className="mb-4">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#d4af37] mb-2">Contactos vinculados a esta oportunidad</div>
                                <div className="orbis-contact-grid grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {linkedContacts.map((c) => <div key={c.id} className="orbis-card-mini p-4 transition"><div className="text-xs font-semibold text-white">{c.full_name || c.first_name}</div><div className="text-[11px] text-[#9fc4df]">{c.position || "Sin cargo"} · {c.role_in_opportunity || c.role_label || "Contacto"}</div><div className="text-[11px] text-[#9fc4df] mt-1">{c.email || "Sin correo"} · {c.mobile || c.phone || "Sin teléfono"}</div>{Number(c.is_primary) === 1 ? <div className="mt-2"><Badge tone="green">Principal</Badge></div> : null}</div>)}
                                </div>
                            </div>
                        ) : <div className="text-[11px] text-[#9fc4df] mb-4">Aún no hay contactos vinculados directamente a esta oportunidad.</div>}

                        {!isNew ? <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                                <Field label="Seleccionar contacto de la cuenta"><select value={selectedContactId} onChange={(e) => setSelectedContactId(e.target.value)} className={inputClass()}><option value="">— Seleccionar contacto —</option>{accountContacts.map((c) => <option key={c.id} value={c.id}>{c.full_name || c.first_name} {c.position ? `· ${c.position}` : ""}</option>)}</select></Field>
                                <Field label="Rol en oportunidad"><input value={contactRole} onChange={(e) => setContactRole(e.target.value)} className={inputClass()} /></Field>
                                <div className="flex items-end"><label className="inline-flex items-center gap-2 text-xs text-[#d9e8f5]"><input type="checkbox" checked={contactPrimary} onChange={(e) => setContactPrimary(e.target.checked)} />Contacto principal</label></div>
                                <div className="flex items-end"><button type="button" onClick={linkSelectedContact} disabled={saving || !selectedContactId} className="w-full orbis-cta-primary px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-60">Vincular contacto</button></div>
                            </div>

                            {showNewContact ? <div className="orbis-card-mini p-4">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#d4af37] mb-3">Nuevo contacto CRM</div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                                    <Field label="Nombre completo *"><input value={newContact.full_name} onChange={(e) => setNewContact((p) => ({ ...p, full_name: e.target.value }))} className={inputClass()} /></Field>
                                    <Field label="Cargo"><input value={newContact.position} onChange={(e) => setNewContact((p) => ({ ...p, position: e.target.value }))} className={inputClass()} /></Field>
                                    <Field label="Área"><input value={newContact.area} onChange={(e) => setNewContact((p) => ({ ...p, area: e.target.value }))} className={inputClass()} /></Field>
                                    <Field label="Rol"><input value={newContact.role_in_opportunity} onChange={(e) => setNewContact((p) => ({ ...p, role_in_opportunity: e.target.value }))} className={inputClass()} /></Field>
                                    <Field label="Correo"><input value={newContact.email} onChange={(e) => setNewContact((p) => ({ ...p, email: e.target.value }))} className={inputClass()} /></Field>
                                    <Field label="WhatsApp / móvil"><input value={newContact.mobile} onChange={(e) => setNewContact((p) => ({ ...p, mobile: e.target.value }))} className={inputClass()} /></Field>
                                    <Field label="Teléfono"><input value={newContact.phone} onChange={(e) => setNewContact((p) => ({ ...p, phone: e.target.value }))} className={inputClass()} /></Field>
                                    <Field label="LinkedIn"><input value={newContact.linkedin} onChange={(e) => setNewContact((p) => ({ ...p, linkedin: e.target.value }))} className={inputClass()} /></Field>
                                    <div className="md:col-span-4 flex justify-end"><button type="button" onClick={createContact} disabled={saving} className="orbis-table-action orbis-table-action-primary disabled:opacity-60">Crear y vincular contacto</button></div>
                                </div>
                            </div> : null}
                        </div> : null}
                    </SectionCard>
                </div>
                {showValueModal ? (
                    <div className="fixed inset-0 z-50 bg-[#010b14]/75 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-[#04192b] rounded-2xl shadow-2xl border border-[#0b4d78] w-full max-w-xl">
                            <div className="orbis-modal-header px-5 py-4 flex items-start justify-between gap-3">
                                <div>
                                    <h3 className="text-base font-semibold text-white">Capturar valor comercial</h3>
                                    <p className="text-[11px] text-[#9fc4df]">Actualiza valor piloto, potencial mensual y valor estimado.</p>
                                </div>
                                <button type="button" onClick={() => setShowValueModal(false)} className="text-xs text-[#9fc4df] hover:text-white">Cerrar</button>
                            </div>
                            <form onSubmit={saveCommercialValueQuick} className="p-5 space-y-3 text-xs">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <Field label="Valor piloto"><input type="number" min="0" value={valueDraft.pilot_value} onChange={(e) => setValueDraft((p) => ({ ...p, pilot_value: e.target.value }))} className={inputClass()} /></Field>
                                    <Field label="Potencial mensual"><input type="number" min="0" value={valueDraft.monthly_potential} onChange={(e) => setValueDraft((p) => ({ ...p, monthly_potential: e.target.value }))} className={inputClass()} /></Field>
                                    <Field label="Valor estimado general"><input type="number" min="0" value={valueDraft.estimated_value} onChange={(e) => setValueDraft((p) => ({ ...p, estimated_value: e.target.value }))} className={inputClass()} /></Field>
                                </div>
                                <div className="orbis-actions-bar p-3 flex justify-end gap-2">
                                    <button type="button" onClick={() => setShowValueModal(false)} className="orbis-table-action">Cancelar</button>
                                    <button type="submit" disabled={saving} className="orbis-table-action orbis-table-action-primary disabled:opacity-60">{saving ? "Guardando..." : "Guardar valor"}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                ) : null}

                {showNextActionModal ? (
                    <div className="fixed inset-0 z-50 bg-[#010b14]/75 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="orbis-modal-panel rounded-[24px] w-full max-w-2xl">
                            <div className="orbis-modal-header px-5 py-4 flex items-start justify-between gap-3">
                                <div>
                                    <h3 className="text-base font-semibold text-white">Definir próxima acción</h3>
                                    <p className="text-[11px] text-[#9fc4df]">Crea una acción clara para que la oportunidad no se enfríe.</p>
                                </div>
                                <button type="button" onClick={() => setShowNextActionModal(false)} className="text-xs text-[#9fc4df] hover:text-white">Cerrar</button>
                            </div>
                            <form onSubmit={saveNextActionQuick} className="p-5 space-y-3 text-xs">
                                <Field label="Próxima acción *"><input value={nextActionDraft.next_action} onChange={(e) => setNextActionDraft((p) => ({ ...p, next_action: e.target.value }))} className={inputClass()} /></Field>
                                <Field label="Fecha próxima acción *"><input type="datetime-local" value={nextActionDraft.next_action_date} onChange={(e) => setNextActionDraft((p) => ({ ...p, next_action_date: e.target.value }))} className={inputClass()} /></Field>
                                <Field label="Comentario comercial"><textarea rows={3} value={nextActionDraft.status_comment} onChange={(e) => setNextActionDraft((p) => ({ ...p, status_comment: e.target.value }))} className={`${inputClass()} resize-none`} /></Field>
                                <div className="orbis-actions-bar p-3 flex justify-end gap-2">
                                    <button type="button" onClick={() => setShowNextActionModal(false)} className="orbis-table-action">Cancelar</button>
                                    <button type="submit" disabled={saving} className="orbis-table-action orbis-table-action-primary disabled:opacity-60">{saving ? "Guardando..." : "Guardar próxima acción"}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                ) : null}

                {showDemoModal ? (
                    <div className="fixed inset-0 z-50 bg-[#010b14]/75 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="orbis-modal-panel rounded-[24px] w-full max-w-3xl">
                            <div className="orbis-modal-header px-5 py-4 flex items-start justify-between gap-3">
                                <div>
                                    <h3 className="text-base font-semibold text-white">Agendar demo guiada ORBIS PMO</h3>
                                    <p className="text-[11px] text-[#9fc4df]">
                                        Crea una actividad tipo Demo ORBIS PMO, guarda el link de reunión y mueve la oportunidad a Reunión agendada.
                                    </p>
                                </div>
                                <button type="button" onClick={() => setShowDemoModal(false)} className="text-xs text-[#9fc4df] hover:text-white">Cerrar</button>
                            </div>

                            <form onSubmit={scheduleGuidedDemo} className="p-5 space-y-3 text-xs">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                    <div className="md:col-span-2">
                                        <Field label="Fecha y hora de demo *">
                                            <input
                                                type="datetime-local"
                                                value={demoDraft.start_datetime}
                                                onChange={(e) => setDemoDraft((p) => ({ ...p, start_datetime: e.target.value }))}
                                                className={inputClass()}
                                            />
                                        </Field>
                                    </div>

                                    <Field label="Duración">
                                        <select
                                            value={demoDraft.duration_minutes}
                                            onChange={(e) => setDemoDraft((p) => ({ ...p, duration_minutes: e.target.value }))}
                                            className={inputClass()}
                                        >
                                            <option value="30">30 minutos</option>
                                            <option value="45">45 minutos</option>
                                            <option value="60">60 minutos</option>
                                            <option value="90">90 minutos</option>
                                        </select>
                                    </Field>

                                    <Field label="Canal *">
                                        <select
                                            value={demoDraft.channel}
                                            onChange={(e) => setDemoDraft((p) => ({ ...p, channel: e.target.value }))}
                                            className={inputClass()}
                                        >
                                            <option value="meet">Google Meet</option>
                                            <option value="zoom">Zoom</option>
                                            <option value="otro">Microsoft Teams / Otro</option>
                                            <option value="presencial">Presencial</option>
                                        </select>
                                    </Field>

                                    <div className="md:col-span-4">
                                        <Field label="Link de reunión / ubicación">
                                            <input
                                                value={demoDraft.meeting_link}
                                                onChange={(e) => setDemoDraft((p) => ({ ...p, meeting_link: e.target.value }))}
                                                className={inputClass()}
                                                placeholder="https://meet.google.com/... / https://zoom.us/... / Microsoft Teams / sala"
                                            />
                                        </Field>
                                    </div>

                                    <div className="md:col-span-4">
                                        <Field label="Objetivo de la demo">
                                            <textarea
                                                rows={3}
                                                value={demoDraft.objective}
                                                onChange={(e) => setDemoDraft((p) => ({ ...p, objective: e.target.value }))}
                                                className={`${inputClass()} resize-none`}
                                            />
                                        </Field>
                                    </div>

                                    <div className="md:col-span-4">
                                        <Field label="Notas internas para preparar la demo">
                                            <textarea
                                                rows={3}
                                                value={demoDraft.notes}
                                                onChange={(e) => setDemoDraft((p) => ({ ...p, notes: e.target.value }))}
                                                className={`${inputClass()} resize-none`}
                                            />
                                        </Field>
                                    </div>

                                    <div className="md:col-span-3">
                                        <Field label="Siguiente acción posterior">
                                            <input
                                                value={demoDraft.next_action}
                                                onChange={(e) => setDemoDraft((p) => ({ ...p, next_action: e.target.value }))}
                                                className={inputClass()}
                                            />
                                        </Field>
                                    </div>

                                    <Field label="Fecha siguiente acción">
                                        <input
                                            type="datetime-local"
                                            value={demoDraft.next_action_date}
                                            onChange={(e) => setDemoDraft((p) => ({ ...p, next_action_date: e.target.value }))}
                                            className={inputClass()}
                                        />
                                    </Field>
                                </div>

                                <div className="orbis-inline-info rounded-2xl p-3 text-[11px]">
                                    Al guardar, la oportunidad se moverá a <strong>Reunión agendada</strong>, se actualizará la probabilidad a 30% y se creará una actividad <strong>Demo ORBIS PMO</strong>.
                                </div>

                                <div className="orbis-actions-bar p-3 flex justify-end gap-2">
                                    <button type="button" onClick={() => setShowDemoModal(false)} className="orbis-table-action">Cancelar</button>
                                    <button type="submit" disabled={saving} className="orbis-table-action orbis-table-action-primary disabled:opacity-60">
                                        {saving ? "Agendando..." : "Agendar demo guiada"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                ) : null}

                {showActivityModal ? (
                    <div className="fixed inset-0 z-50 bg-[#010b14]/75 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="orbis-modal-panel rounded-[24px] w-full max-w-3xl">
                            <div className="orbis-modal-header px-5 py-4 flex items-start justify-between gap-3">
                                <div>
                                    <h3 className="text-base font-semibold text-white">Crear actividad comercial</h3>
                                    <p className="text-[11px] text-[#9fc4df]">Agenda una acción vinculada a esta oportunidad.</p>
                                </div>
                                <button type="button" onClick={() => setShowActivityModal(false)} className="text-xs text-[#9fc4df] hover:text-white">Cerrar</button>
                            </div>
                            <form onSubmit={createActivityQuick} className="p-5 space-y-3 text-xs">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div className="md:col-span-2"><Field label="Título *"><input value={activityDraft.title} onChange={(e) => setActivityDraft((p) => ({ ...p, title: e.target.value }))} className={inputClass()} /></Field></div>
                                    <Field label="Fecha *"><input type="datetime-local" value={activityDraft.start_datetime} onChange={(e) => setActivityDraft((p) => ({ ...p, start_datetime: e.target.value }))} className={inputClass()} /></Field>
                                    <Field label="Tipo"><select value={activityDraft.type} onChange={(e) => setActivityDraft((p) => ({ ...p, type: e.target.value }))} className={inputClass()}><option value="seguimiento_propuesta">Seguimiento propuesta</option><option value="demo_orbis">Demo ORBIS</option><option value="reunion_virtual">Reunión virtual</option><option value="reunion_presencial">Reunión presencial</option><option value="llamada">Llamada</option><option value="whatsapp">WhatsApp</option><option value="correo">Correo</option></select></Field>
                                    <Field label="Canal"><select value={activityDraft.channel} onChange={(e) => setActivityDraft((p) => ({ ...p, channel: e.target.value }))} className={inputClass()}><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="telefono">Teléfono</option><option value="meet">Meet</option><option value="presencial">Presencial</option><option value="linkedin">LinkedIn</option></select></Field>
                                    <div className="md:col-span-3"><Field label="Objetivo"><textarea rows={3} value={activityDraft.objective} onChange={(e) => setActivityDraft((p) => ({ ...p, objective: e.target.value }))} className={`${inputClass()} resize-none`} /></Field></div>
                                    <div className="md:col-span-2"><Field label="Siguiente acción opcional"><input value={activityDraft.next_action} onChange={(e) => setActivityDraft((p) => ({ ...p, next_action: e.target.value }))} className={inputClass()} /></Field></div>
                                    <Field label="Fecha siguiente acción"><input type="datetime-local" value={activityDraft.next_action_date} onChange={(e) => setActivityDraft((p) => ({ ...p, next_action_date: e.target.value }))} className={inputClass()} /></Field>
                                </div>
                                <div className="orbis-actions-bar p-3 flex justify-end gap-2">
                                    <button type="button" onClick={() => setShowActivityModal(false)} className="orbis-table-action">Cancelar</button>
                                    <button type="submit" disabled={saving} className="orbis-table-action orbis-table-action-primary disabled:opacity-60">{saving ? "Guardando..." : "Crear actividad"}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                ) : null}

            </div>
            </div>
        </MainLayout>
    );
}
